import type { ApprovalInboxPaymentScope } from '@metycle/shared';
import type { NavBadgeKey } from '@/config/navigation-types';
import { useApprovalsInboxCount } from '@/features/approvals/inbox';
import { cn } from '@/lib/utils';

interface Props {
  badgeKey: NavBadgeKey;
  className?: string;
}

// The main Approvals badge counts everything except invoice (payment) approvals;
// the Payment Approvals badge counts only those — so the two never double-count.
const PAYMENT_SCOPE_BY_BADGE: Record<NavBadgeKey, ApprovalInboxPaymentScope> = {
  'approvals-inbox': 'exclude',
  'payment-approvals-inbox': 'only',
};

export function NavBadge({ badgeKey, className }: Readonly<Props>) {
  const inbox = useApprovalsInboxCount(PAYMENT_SCOPE_BY_BADGE[badgeKey]);
  const count = inbox.data?.count ?? 0;
  if (count <= 0) return null;
  return (
    <span
      data-slot="nav-badge"
      className={cn(
        'ml-auto inline-flex h-5 min-w-5 shrink-0 items-center justify-center',
        'rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground',
        className,
      )}
      aria-label={`${String(count)} pending`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
